import React, {useEffect, useMemo} from "react";
import {FormProvider, useForm, useWatch} from "react-hook-form";
import {
   Box,
   Button,
   Dialog,
   DialogActions,
   DialogContent,
   DialogTitle,
   Typography
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {FormInputText} from "../../shop/wholesale/form/FormInputText";
import {FormInputSelect} from "../../shop/wholesale/form/FormInputSelect";
import {FormInputDate} from "../../shop/wholesale/form/FormInputDate";
import {FormInputCheckbox} from "../../shop/wholesale/form/FormInputCheckbox";
import {
   CreateOneParcelInput,
   EnumAddressInfoRefType,
   useParcelsPageCarriersLazyQuery,
   useParcelsPageCreateMutation,
   useParcelsPageLogisticsPartnerQuery,
   useParcelsPageShopsQuery,
} from "../../../Queries";
import {SavingUI} from "../../../SavingProvider";
import dayjs from "dayjs";

interface ParcelCreateDialogProps {
   open: boolean;
   onClose: () => void;
   onCreated: () => void;
   brandHouseId: string;
   shopId?: string;
}

interface AddressFormData {
   name: string;
   attention: string;
   add1: string;
   add2: string;
   zipCode: string;
   city: string;
   countryCode: string;
   mail: string;
   phone: string;
}

interface FormData {
   shopId: string;
   parcelDate: string;
   shippingCarrierId: string;
   manualTracking: boolean;
   packageCode: string;
   comment: string;
   sender: AddressFormData;
   receiver: AddressFormData;
}

const emptyAddress: AddressFormData = {
   name: "",
   attention: "",
   add1: "",
   add2: "",
   zipCode: "",
   city: "",
   countryCode: "",
   mail: "",
   phone: "",
};

const defaultValues: FormData = {
   shopId: "",
   parcelDate: dayjs().format("YYYY-MM-DD"),
   shippingCarrierId: "",
   manualTracking: false,
   packageCode: "",
   comment: "",
   sender: {...emptyAddress},
   receiver: {...emptyAddress},
};

const AddressFields = ({prefix, label}: { prefix: string; label: string }) => {
   return <Box sx={{mb: 2}}>
      <Typography variant="subtitle2" sx={{mb: 1, mt: 1}}>{label}</Typography>
      <FormInputText name={`${prefix}.name`} label="Name / Company"/>
      <FormInputText name={`${prefix}.attention`} label="Attention"/>
      <FormInputText name={`${prefix}.add1`} label="Address"/>
      <FormInputText name={`${prefix}.add2`} label="Address 2"/>
      <Grid container columnGap="8px" wrap="nowrap">
         <Grid item xs={4}>
            <FormInputText name={`${prefix}.zipCode`} label="Zipcode"/>
         </Grid>
         <Grid item xs={8}>
            <FormInputText name={`${prefix}.city`} label="City"/>
         </Grid>
      </Grid>
      <FormInputText name={`${prefix}.countryCode`} label="Country code (ISO)"/>
      <Grid container columnGap="8px" wrap="nowrap">
         <Grid item xs={6}>
            <FormInputText name={`${prefix}.mail`} label="Mail"/>
         </Grid>
         <Grid item xs={6}>
            <FormInputText name={`${prefix}.phone`} label="Phone"/>
         </Grid>
      </Grid>
   </Box>;
};

export const ParcelCreateDialog = ({open, onClose, onCreated, brandHouseId, shopId: fixedShopId}: ParcelCreateDialogProps) => {
   const [createParcel] = useParcelsPageCreateMutation();
   const [fetchCarriers, {data: carriersData}] = useParcelsPageCarriersLazyQuery();

   const {data: shopsData} = useParcelsPageShopsQuery({
      variables: {brandHouseId},
   });

   const shops = shopsData?.ShopMany || [];

   const shopOptions = useMemo(() => {
      return shops.map(shop => ({id: shop._id, name: shop.name || shop._id}));
   }, [shops]);

   const methods = useForm<FormData>({
      defaultValues: {
         ...defaultValues,
         shopId: fixedShopId || "",
      }
   });
   const {handleSubmit, reset, control} = methods;

   const selectedShopId = useWatch({control, name: "shopId"});
   const effectiveShopId = fixedShopId || selectedShopId;
   const manualTracking = useWatch({control, name: "manualTracking"});
   const receiverZipCode = useWatch({control, name: "receiver.zipCode"});
   const receiverCountryCode = useWatch({control, name: "receiver.countryCode"});
   const receiverCity = useWatch({control, name: "receiver.city"});

   const selectedShop = useMemo(() => {
      return shops.find(s => s._id === effectiveShopId);
   }, [shops, effectiveShopId]);

   const {data: lpData} = useParcelsPageLogisticsPartnerQuery({
      variables: {id: selectedShop?.logisticsPartnerId!},
      skip: !selectedShop?.logisticsPartnerId,
   });
   const lpCountryCode = lpData?.LogisticsPartner?.countryCode;

   // Fetch carriers when dialog opens and when receiver address changes
   useEffect(() => {
      if (!open || !effectiveShopId) return;

      const receiver = manualTracking
         ? (lpCountryCode ? {countryCode: lpCountryCode} : undefined)
         : ((receiverZipCode || receiverCountryCode || receiverCity)
            ? {zipCode: receiverZipCode, countryCode: receiverCountryCode, city: receiverCity}
            : undefined);

      fetchCarriers({variables: {shopId: effectiveShopId, receiver}});
   }, [open, effectiveShopId, manualTracking, lpCountryCode, fetchCarriers, receiverZipCode, receiverCountryCode, receiverCity]);

   const carrierOptions = useMemo(() => {
      return (carriersData?.ShippingCarriersForParcel || [])
         .filter((c): c is NonNullable<typeof c> => c != null)
         .map(c => ({id: c._id, name: c.name || c._id}));
   }, [carriersData]);

   const toAddressInput = (addr: AddressFormData) => {
      return {
         name: addr.name || undefined,
         attention: addr.attention || undefined,
         add1: addr.add1 || undefined,
         add2: addr.add2 || undefined,
         zipCode: addr.zipCode || undefined,
         city: addr.city || undefined,
         countryCode: addr.countryCode || undefined,
         mail: addr.mail || undefined,
         phone: addr.phone || undefined,
      };
   };

   const resetForm = () => {
      reset({...defaultValues, shopId: fixedShopId || ""});
   };

   const onSubmit = async (data: FormData) => {
      const isManual = data.manualTracking && data.packageCode;

      const sender = isManual
         ? {refId: effectiveShopId, refType: EnumAddressInfoRefType.SHOP}
         : toAddressInput(data.sender);

      const receiver = isManual && selectedShop?.logisticsPartnerId
         ? {refId: selectedShop.logisticsPartnerId, refType: EnumAddressInfoRefType.LOGISTICSPARTNER}
         : toAddressInput(data.receiver);

      const input: CreateOneParcelInput = {
         brandHouseId,
         shopId: effectiveShopId || undefined,
         parcelDate: data.parcelDate,
         comment: data.comment || undefined,
         shippingCarrierId: data.shippingCarrierId || undefined,
         sender,
         receiver,
         ...(isManual ? {manualTracking: {packageCode: data.packageCode}} : {}),
      };

      await SavingUI.process(async () => {
         await createParcel({variables: {input}});
      }, "Parcel created successfully");

      resetForm();
      onCreated();
   };

   const handleClose = () => {
      resetForm();
      onClose();
   };

   return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
         <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
               <DialogTitle>Create Parcel</DialogTitle>
               <DialogContent>
                  <Box sx={{pt: 1}}>
                     {!fixedShopId && (
                        <FormInputSelect
                           name="shopId"
                           label="Shop"
                           options={shopOptions}
                           required
                        />
                     )}

                     <FormInputDate name="parcelDate" label="Parcel date"/>

                     <FormInputCheckbox name="manualTracking" label="Manual tracking (I already have a tracking number)"/>

                     {manualTracking && (
                        <FormInputText name="packageCode" label="Tracking number / Package code"/>
                     )}

                     {!manualTracking && (
                        <Grid container spacing={2}>
                           <Grid item xs={6}>
                              <AddressFields prefix="sender" label="Sender"/>
                           </Grid>
                           <Grid item xs={6}>
                              <AddressFields prefix="receiver" label="Receiver"/>
                           </Grid>
                        </Grid>
                     )}

                     <FormInputSelect
                        name="shippingCarrierId"
                        label="Shipping carrier"
                        options={carrierOptions}
                     />

                     <FormInputText name="comment" label="Comment" multiline rows={3}/>
                  </Box>
               </DialogContent>
               <DialogActions>
                  <Button onClick={handleClose}>Cancel</Button>
                  <Button type="submit" variant="contained" color="primary">Create</Button>
               </DialogActions>
            </form>
         </FormProvider>
      </Dialog>
   );
};
